import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavController, PopoverController } from '@ionic/angular';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { ChartDataset, ChartOptions } from 'chart.js';
import _ from 'lodash';
import moment from 'moment';

import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { CONVERSATION_STATUS, routes } from 'src/app/constants';
import { getRandomColor } from 'src/app/utils/utils';
import { OnboardingProgress, WhereClause } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { ConversationsService } from 'src/services/conversations.service';
import { ServerEventsService, ServerEventSubscription } from 'src/services/server-events.service';
import { SlackService } from 'src/services/slack.service';

const DAY_OF_WEEK_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

@Component({
  selector: 'app-cx-dashboard',
  templateUrl: './cx-dashboard.component.html',
  styleUrls: ['./cx-dashboard.component.scss'],
})
export class CxDashboardComponent implements OnInit, OnDestroy {
  isInitialized = false;
  isLoading = false;
  isTableView = true;

  medianFirstResponse: string;
  medianFirstResponseLabel: string;
  medianResolution: string;
  medianResolutionLabel: string;
  avgTicketsPerDay: string;

  myNeedsResponse: string;
  myInProgress: string;
  myOnHold: string;
  allNeedsResponse: string;
  allInProgress: string;
  allOnHold: string;

  openCount: number;
  inProgressCount: number;
  onHoldCount: number;

  ColumnMode = ColumnMode;

  slackScopeRequired: boolean;

  searchText: string;

  showOnboarding = false;
  onboardingLabel: string;
  onboardingNextStep: string;
  onboardingPercentage: number;
  onboardingProgress: OnboardingProgress;

  subscription: ServerEventSubscription;

  // Line chart of conversations over time
  public conversationsChartData: ChartDataset[] = [
    {
      fill: true,
      data: [],
      tension: 0,
      label: 'Conversations opened',
      backgroundColor: (ctx, _options) => {
        const grad = ctx.chart.ctx.createLinearGradient(0, 0, 0, 400);
        grad.addColorStop(0, 'rgba(0, 128, 255, 0.56)');
        grad.addColorStop(1, 'rgba(0, 128, 255, 0)');
        ctx.chart.ctx.fillStyle = grad;
        return grad;
      },
      borderColor: 'rgba(0, 128, 255, 1)',
      pointBackgroundColor: 'rgba(0, 128, 255, 1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(0, 128, 255, 1)',
    },
  ];
  public conversationsChartLabels: string[] = [];
  public conversationsChartOptions: ChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        offset: false,
        grid: {
          display: false,
        },
      },
      y: {
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  // Pie chart of customers
  public customersPieData: ChartDataset<'pie'>[];
  public customersPieLabels: string[];
  public customersPieOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
      },
    },
  };

  // Pie chart of tags
  public tagsPieData: ChartDataset<'pie'>[] = [
    {
      data: [],
      backgroundColor: '#d6c8ff',
      borderColor: '#9570ff',
      hoverBorderColor: '#fff',
    },
  ];
  public tagsPieLabels: string[];
  public tagsPieOptions: ChartOptions = {
    indexAxis: 'y',
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        offset: false,
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  // Pie chart of assignees
  public assigneePieData: ChartDataset<'pie'>[] = [
    {
      data: [],
      hoverBorderColor: '#fff',
    },
  ];
  public assigneePieLabels: string[];
  public assigneePieOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  // Bar chart of ticket types
  public ticketTypesPieData: ChartDataset<'pie'>[] = [
    {
      data: [],
      backgroundColor: (ctx, _options) => {
        const grad = ctx.chart.ctx.createLinearGradient(0, 0, 0, 400);
        grad.addColorStop(0, 'rgba(34, 197, 94, 0.8)');
        grad.addColorStop(1, 'rgba(34, 197, 94, 0.2)');
        ctx.chart.ctx.fillStyle = grad;
        return grad;
      },
      borderColor: 'rgba(34, 197, 94, 1)',
      hoverBorderColor: '#fff',
    },
  ];
  public ticketTypesPieLabels: string[];
  public ticketTypesPieOptions: ChartOptions = {
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        offset: false,
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  // Bar chart of messages by day of week
  public dayOfWeekLabels = DAY_OF_WEEK_LABELS;
  public messagesByDowDatasets: ChartDataset[] = [];
  public messagesByDowOptions: ChartOptions = {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        displayColors: false,
        callbacks: {
          label: (tooltipItem) => {
            return `${tooltipItem.formattedValue} new message${
              tooltipItem.formattedValue === '1' ? '' : 's'
            } per day on average`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          stepSize: 1,
        },
      },
      y: {
        display: false,
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  dateRangeDays = 30;
  isDisplayingMedian = true;

  constructor(
    private navController: NavController,
    private popoverCtrl: PopoverController,
    private apiService: ApiService,
    private slackService: SlackService,
    public authService: AuthService,
    private serverEventsService: ServerEventsService,
    private conversationsService: ConversationsService,
  ) {}

  async ionViewWillEnter() {
    const tenant = this.authService.tenant;

    if (!this.authService.integrations.slackEnabled) {
      this.navController.navigateRoot('onboarding');
      return;
    }

    if (tenant?.accountStatus === 'trial' && this.authService.isTenantAdmin) {
      const end = moment(tenant.trialEndDate);
      const trialExpired = end.diff(moment()) < 0;
      if (trialExpired && !this.authService.user?.email?.includes('@unthread.io')) {
        this.navController.navigateRoot('upgrade');
        return;
      }
    }

    this.isLoading = true;

    this.slackScopeRequired = await this.slackService.requiresScope(true);

    await this.getOnboardingProgress();
    await this.fetchAllData();

    this.isLoading = false;
    this.isInitialized = true;
  }

  ngOnInit(): void {
    this.subscription = this.serverEventsService.subscribe(
      ['conversation:created', 'conversation:changed_status', 'conversation:reassigned', 'conversation:escalated'],
      _.debounce(() => this.getConversationCounts(), 1000),
    );

    this.isDisplayingMedian = localStorage.getItem('homeResponseStatistic') === 'average' ? false : true;
    this.dateRangeDays = Number(localStorage.getItem('homeDateRangeDays')) || 30;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  async getOnboardingProgress() {
    if (!this.authService.isTenantAdmin) {
      this.showOnboarding = false;
      return;
    }

    try {
      this.onboardingProgress = await this.apiService.getPromise('/onboarding-progress');

      const quickstartStepsCompleted =
        Number(this.onboardingProgress.isSlackConnected) +
        Number(this.onboardingProgress.isCustomerAdded) +
        Number(this.onboardingProgress.isAssignmentRuleSet) +
        Number(this.onboardingProgress.isTriageChannelSet) +
        Number(this.onboardingProgress.isAdminAdded);

      const advancedStepsCompleted =
        Number(this.onboardingProgress.isEmailConnected) +
        Number(this.onboardingProgress.isWidgetConnected) +
        Number(this.onboardingProgress.isCRMConnected) +
        Number(this.onboardingProgress.isTaskManagerConnected) +
        Number(this.onboardingProgress.isIntegrationAdded);

      const aiStepsCompleted =
        Number(this.onboardingProgress.isArticleAdded) +
        Number(this.onboardingProgress.isTagAdded) +
        Number(this.onboardingProgress.isAutomationAdded);

      if (quickstartStepsCompleted < 5) {
        if (!this.onboardingProgress.isCustomerAdded) {
          this.onboardingNextStep = 'Next step: add your customers to start tracking conversations';
        } else if (!this.onboardingProgress.isAssignmentRuleSet) {
          this.onboardingNextStep = 'Next step: set up SLAs';
        } else if (!this.onboardingProgress.isTriageChannelSet) {
          this.onboardingNextStep = 'Next step: set up a triage channel';
        } else if (!this.onboardingProgress.isAdminAdded) {
          this.onboardingNextStep = 'Next step: add your team';
        }

        this.onboardingPercentage = Math.round((quickstartStepsCompleted / 5) * 100);
        this.onboardingLabel = `Quickstart setup (${quickstartStepsCompleted} of 5 done)`;
        this.showOnboarding = true;
      } else if (aiStepsCompleted < 3) {
        if (!this.onboardingProgress.isArticleAdded) {
          this.onboardingNextStep = 'Next step: add your knowledge base to train the AI';
        } else if (!this.onboardingProgress.isTagAdded) {
          this.onboardingNextStep = 'Next step: add tags to auto-categorize requests';
        } else if (!this.onboardingProgress.isAutomationAdded) {
          this.onboardingNextStep = 'Next step: add an automated workflow';
        }

        this.onboardingPercentage = Math.round((aiStepsCompleted / 3) * 100);
        this.onboardingLabel = `AI and automation setup (${aiStepsCompleted} of 3 done)`;
        this.showOnboarding = true;
      } else {
        if (advancedStepsCompleted < 5) {
          this.onboardingPercentage = Math.round(((advancedStepsCompleted + quickstartStepsCompleted) / 10) * 100);
          const stepsRemaining = 5 - advancedStepsCompleted;
          this.onboardingLabel = `${
            this.authService.tenant?.accountStatus === 'trial'
              ? 'Add AI, Automations, and More'
              : 'Account setup recommendations'
          }: ${stepsRemaining} step${stepsRemaining === 1 ? '' : 's'} left`;
          this.onboardingNextStep = !this.onboardingProgress.isTaskManagerConnected
            ? `Connect to a task manager like Jira, Asana, Linear, others`
            : !this.onboardingProgress.isCRMConnected
              ? `Connect to a CRM like Salesforce or Hubspot`
              : !this.onboardingProgress.isEmailConnected
                ? `Respond to support emails from Unthread`
                : !this.onboardingProgress.isWidgetConnected
                  ? `Embed live chat in your web app`
                  : ``;
          this.showOnboarding = true;
        } else {
          this.showOnboarding = false;
        }
      }
    } catch (e) {}
  }

  fetchAllData() {
    return Promise.all([this.analyzeConversationList(), this.getConversationCounts(), this.getMessageCountByDow()]);
  }

  async getConversationCounts() {
    const limit = 0;

    const baseWhere: WhereClause[] = this.authService.selectedProject?.id
      ? [
          {
            field: 'projectId',
            operator: '==',
            value: this.authService.selectedProject.id,
          },
        ]
      : [];

    const [myNeedsResponse, myInProgress, myOnHold, allNeedsResponse, allInProgress, allOnHold] = await Promise.all([
      this.conversationsService.list({
        limit,
        select: ['id'],
        where: [
          ...baseWhere,
          {
            field: 'status',
            operator: '==',
            value: CONVERSATION_STATUS.OPEN,
          },
          {
            field: 'assignedToUserId',
            operator: '==',
            value: this.authService.profile.id,
          },
        ],
      }),
      this.conversationsService.list({
        limit,
        select: ['id'],
        where: [
          ...baseWhere,
          {
            field: 'status',
            operator: '==',
            value: CONVERSATION_STATUS.IN_PROGRESS,
          },
          {
            field: 'assignedToUserId',
            operator: '==',
            value: this.authService.profile.id,
          },
        ],
      }),
      this.conversationsService.list({
        limit,
        select: ['id'],
        where: [
          ...baseWhere,
          {
            field: 'status',
            operator: '==',
            value: CONVERSATION_STATUS.ON_HOLD,
          },
          {
            field: 'assignedToUserId',
            operator: '==',
            value: this.authService.profile.id,
          },
        ],
      }),
      this.conversationsService.list({
        limit,
        select: ['id'],
        where: [
          ...baseWhere,
          {
            field: 'status',
            operator: '==',
            value: CONVERSATION_STATUS.OPEN,
          },
        ],
      }),
      this.conversationsService.list({
        limit,
        select: ['id'],
        where: [
          ...baseWhere,
          {
            field: 'status',
            operator: '==',
            value: CONVERSATION_STATUS.IN_PROGRESS,
          },
        ],
      }),
      this.conversationsService.list({
        limit,
        select: ['id'],
        where: [
          ...baseWhere,
          {
            field: 'status',
            operator: '==',
            value: CONVERSATION_STATUS.ON_HOLD,
          },
        ],
      }),
    ]);

    this.myNeedsResponse = `${myNeedsResponse.totalCount}`;
    this.myInProgress = `${myInProgress.totalCount}`;
    this.myOnHold = `${myOnHold.totalCount}`;
    this.allNeedsResponse = `${allNeedsResponse.totalCount}`;
    this.allInProgress = `${allInProgress.totalCount}`;
    this.allOnHold = `${allOnHold.totalCount}`;
  }

  async getMessageCountByDow() {
    const timezone = moment().tz(moment.tz.guess()).format('z');
    const data: any[] = await this.apiService.getPromise(
      `/reporting/message-count-by-period?groupBy=dow&timezone=${timezone}`,
    );
    const messagesByDowRawData = {};

    for (const point of data) {
      if (point.averageCount) {
        messagesByDowRawData[point.dow] = point.averageCount;
      }
    }

    const messagesByDowData = [];

    for (let x = 0; x < 7; x++) {
      const averageCount = messagesByDowRawData[x];

      messagesByDowData.push(averageCount);
    }

    this.messagesByDowDatasets = [
      {
        data: messagesByDowData,
        backgroundColor: 'rgba(34, 57, 237, 0.8)',
        hoverBorderColor: '#fff',
      },
    ];
  }

  async analyzeConversationList() {
    this.customersPieData = [{ data: [], hoverBorderColor: '#fff' }];
    this.customersPieLabels = [];
    this.tagsPieData[0].data = [];
    this.tagsPieLabels = [];
    this.ticketTypesPieData[0].data = [];
    this.ticketTypesPieLabels = [];

    const customerData = {};
    const convMap = {};
    const dailyConvos = [];
    let totalConversations = 0;
    const labels = [];
    const startDate = moment().startOf('day').subtract(this.dateRangeDays, 'days');
    const startDateIso = startDate.format('YYYY-MM-DD');
    const endDateIso = moment().endOf('day').format('YYYY-MM-DD');
    const timezone = moment.tz.guess();
    const filters = this.authService.selectedProject?.id
      ? {
          projectId: this.authService.selectedProject.id,
        }
      : {};

    const [
      responseTimeMedianReport,
      resolutionTimeMedianReport,
      totalCountByCustomerReport,
      totalCountByTagReport,
      totalCountByAssigneeReport,
      totalCountByTicketTypeReport,
    ]: any[] = await Promise.all([
      this.apiService.postPromise(`/reporting/time-series`, {
        startDate: startDateIso,
        endDate: endDateIso,
        metric: this.isDisplayingMedian ? 'responseTimeWorkingMedian' : 'responseTimeWorkingMean',
        timezone,
        filters,
      }),
      this.apiService.postPromise(`/reporting/time-series`, {
        startDate: startDateIso,
        endDate: endDateIso,
        metric: this.isDisplayingMedian ? 'resolutionTimeWorkingMedian' : 'resolutionTimeWorkingMean',
        timezone,
        filters,
      }),
      this.apiService.postPromise(`/reporting/time-series`, {
        dateDimension: 'day',
        startDate: startDateIso,
        endDate: endDateIso,
        metric: 'totalCount',
        dimensions: ['customerId'],
        timezone,
        filters,
      }),
      this.apiService.postPromise(`/reporting/time-series`, {
        dateDimension: 'quarter',
        startDate: startDateIso,
        endDate: endDateIso,
        metric: 'totalCount',
        dimensions: ['tagId'],
        timezone,
        filters,
      }),
      this.apiService.postPromise(`/reporting/time-series`, {
        dateDimension: 'day',
        startDate: startDateIso,
        endDate: endDateIso,
        metric: 'totalCount',
        dimensions: ['assigneeId'],
        timezone,
        filters,
      }),
      this.apiService.postPromise(`/reporting/time-series`, {
        dateDimension: 'quarter',
        startDate: startDateIso,
        endDate: endDateIso,
        metric: 'totalCount',
        dimensions: ['ticketTypeId'],
        timezone,
        filters,
      }),
    ]);

    this.parseTagData(totalCountByTagReport);
    this.parseAssigneeData(totalCountByAssigneeReport);
    this.parseTicketTypeData(totalCountByTicketTypeReport);

    const customerMap = totalCountByCustomerReport.lookups.customerId;

    for (const [customerId, data] of Object.entries(totalCountByCustomerReport.data)) {
      const customerName = customerMap[customerId];

      for (const [date, count] of Object.entries(data)) {
        convMap[date] = (convMap[date] ?? 0) + count;

        if (customerId !== 'null' && customerName) {
          customerData[customerName] = (customerData[customerName] ?? 0) + count;
        }
      }
    }

    let i = 0;

    for (const day of totalCountByCustomerReport.dates) {
      const convCount = convMap[day] ?? 0;

      dailyConvos[i] = convCount;
      totalConversations += convCount;
      labels.push(moment(day).format('M/D'));

      i += 1;
    }

    this.customersPieData[0].data = _.values(customerData);
    this.customersPieData[0].backgroundColor = _.values(customerData).map(() => getRandomColor());
    this.customersPieLabels = _.keys(customerData);
    this.customersPieOptions.plugins.legend.display = this.customersPieLabels?.length < 18;

    this.conversationsChartData[0].data = dailyConvos;
    this.conversationsChartLabels = labels;

    if (!this.isInitialized) {
      const medianFirstResponseSeconds = responseTimeMedianReport.data[responseTimeMedianReport.dates?.[0]] ?? 0;
      const medianFirstResponseMinutes = Math.round(medianFirstResponseSeconds / 60);
      const medianFirstResponseHours = Math.round(medianFirstResponseMinutes / 60);
      const medianFirstResponseDays = Math.round(medianFirstResponseMinutes / 60 / 24);

      if (medianFirstResponseDays) {
        this.medianFirstResponse = `${medianFirstResponseDays}`;
        this.medianFirstResponseLabel = `day${medianFirstResponseDays === 1 ? '' : 's'}`;
      } else if (medianFirstResponseHours) {
        this.medianFirstResponse = `${medianFirstResponseHours}`;
        this.medianFirstResponseLabel = 'hr';
      } else if (medianFirstResponseMinutes) {
        this.medianFirstResponse = `${medianFirstResponseMinutes}`;
        this.medianFirstResponseLabel = `min`;
      } else if (medianFirstResponseMinutes === 0) {
        this.medianFirstResponse = '< 1';
        this.medianFirstResponseLabel = `min`;
      } else {
        this.medianFirstResponse = `n/a`;
        this.medianFirstResponseLabel = ``;
      }

      const medianResolutionSeconds = resolutionTimeMedianReport.data[resolutionTimeMedianReport.dates?.[0]] ?? 0;
      const medianResolutionMinutes = Math.round(medianResolutionSeconds / 60);
      const medianResolutionHours = Math.round(medianResolutionMinutes / 60);
      const medianResolutionDays = Math.round(medianResolutionMinutes / 60 / 24);

      if (medianResolutionDays) {
        this.medianResolution = `${medianResolutionDays}`;
        this.medianResolutionLabel = `day${medianResolutionDays === 1 ? '' : 's'}`;
      } else if (medianResolutionHours) {
        this.medianResolution = `${medianResolutionHours}`;
        this.medianResolutionLabel = 'hr';
      } else if (medianResolutionMinutes) {
        this.medianResolution = `${medianResolutionMinutes}`;
        this.medianResolutionLabel = `min`;
      } else {
        this.medianResolution = `n/a`;
        this.medianResolutionLabel = ``;
      }

      this.avgTicketsPerDay = `${Math.trunc((totalConversations / this.dateRangeDays) * 10) / 10}`;
      this.isInitialized = true;
    }
  }

  parseTagData(reportResponse: any) {
    const tagMap = reportResponse.lookups.tagId;
    const tempMap = {};

    for (const [tagId, data] of Object.entries(reportResponse.data)) {
      const tagName = tagMap[tagId];

      for (const [date, count] of Object.entries(data)) {
        if (tagId !== 'null' && tagName) {
          tempMap[tagName] = (tempMap[tagName] ?? 0) + count;
        }
      }
    }

    this.tagsPieData[0].data = _.values(tempMap);
    this.tagsPieLabels = _.keys(tempMap);
  }

  parseAssigneeData(reportResponse: any) {
    const assigneeMap = reportResponse.lookups.assigneeId;
    const tempMap = {};

    for (const [assigneeId, data] of Object.entries(reportResponse.data)) {
      const assigneeName = assigneeMap[assigneeId];

      for (const [_date, count] of Object.entries(data)) {
        if (assigneeId !== 'null' && assigneeName) {
          tempMap[assigneeName] = (tempMap[assigneeName] ?? 0) + count;
        }
      }
    }

    this.assigneePieData[0].data = _.values(tempMap);
    this.assigneePieData[0].backgroundColor = _.values(tempMap).map(() => getRandomColor());
    this.assigneePieLabels = _.keys(tempMap);
    this.assigneePieOptions.plugins.legend.display = this.assigneePieLabels?.length < 18;
  }

  parseTicketTypeData(reportResponse: any) {
    const ticketTypeMap = reportResponse.lookups.ticketTypeId;
    const tempMap = {};

    for (const [ticketTypeId, data] of Object.entries(reportResponse.data)) {
      const ticketTypeName = ticketTypeMap[ticketTypeId];

      for (const [date, count] of Object.entries(data)) {
        if (ticketTypeId !== 'null' && ticketTypeName) {
          tempMap[ticketTypeName] = (tempMap[ticketTypeName] ?? 0) + count;
        }
      }
    }

    this.ticketTypesPieData[0].data = _.values(tempMap);
    this.ticketTypesPieLabels = _.keys(tempMap);
  }

  generateDummyDemoData() {
    // Top datapoints
    this.medianFirstResponse = `3`;
    this.medianFirstResponseLabel = `min`;

    this.medianResolution = '6.5';
    this.medianResolutionLabel = 'hours';

    this.avgTicketsPerDay = '2.1';

    // Charts
    const days = 30;
    const labels = [];
    const dailyConvos = [0, 0, 1, 3, 2, 1, 0, 0, 3, 5, 2, 3, 2, 0, 0, 3, 4, 4, 5, 6, 1, 0, 0, 5, 4, 5, 6, 4, 3, 1];
    const runningDate = moment().subtract(days, 'days');
    for (let i = 0; i < days; i++) {
      runningDate.add(1, 'days');
      labels.push(runningDate.format('M/D'));
    }

    this.conversationsChartData[0].data = dailyConvos;
    this.conversationsChartLabels = labels;

    this.customersPieData[0].data = [3, 6, 5];
    this.customersPieLabels = ['Dunder Mifflin', 'Sabre', 'Staples'];
  }

  connectSlack(redirectToCustomerPage = false) {
    const redirect = redirectToCustomerPage ? `${window.location.origin}/customers/new` : window.location.href;
    this.slackService.oauth(redirect, window.location.origin);
  }

  explainTracking() {
    window.open('https://docs.unthread.io/account-setup/tracking-convo', '_blank');
  }

  async showDateOptions() {
    const DATE_OPTIONS = [
      {
        label: 'Last 90 days',
        value: 90,
        icon: this.dateRangeDays === 90 ? 'checkmark-outline' : 'lol',
      },
      {
        label: 'Last 30 days',
        value: 30,
        icon: this.dateRangeDays === 30 ? 'checkmark-outline' : 'lol',
      },
      {
        label: 'Last 14 days',
        value: 14,
        icon: this.dateRangeDays === 14 ? 'checkmark-outline' : 'lol',
      },
      {
        label: 'Last 7 days',
        value: 7,
        icon: this.dateRangeDays === 7 ? 'checkmark-outline' : 'lol',
      },
      {
        label: 'More analytics options',
        icon: 'open-outline',
        value: -1,
      },
    ];
    const options: ActionDropdownType[] = DATE_OPTIONS.map((opt) => {
      return {
        label: opt.label,
        icon: opt.icon,
      };
    });

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number) => {
          const option = DATE_OPTIONS[idx];
          if (option.value >= 0) {
            this.dateRangeDays = option.value;
            localStorage.setItem('homeDateRangeDays', `${this.dateRangeDays}`);
            this.isInitialized = false;
            this.fetchAllData();
          } else {
            this.viewAnalytics();
          }

          popover.dismiss();
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async showMedianOptions() {
    const options: ActionDropdownType[] = [
      {
        label: 'Display median',
        icon: this.isDisplayingMedian ? 'checkmark-outline' : 'lol',
      },
      {
        label: 'Display average',
        icon: this.isDisplayingMedian ? 'lol' : 'checkmark-outline',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number) => {
          popover.dismiss();

          this.isInitialized = false;
          this.isDisplayingMedian = idx === 0;

          localStorage.setItem('homeResponseStatistic', this.isDisplayingMedian ? 'median' : 'average');

          this.analyzeConversationList();
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  viewAnalytics(type?: string) {
    if (!this.authService.isAnyProjectOwner()) {
      return;
    }

    let routeAppend = '';

    if (type === 'volume') {
      routeAppend = `/volume`;
    } else if (type === 'response') {
      routeAppend = `?chartType=responseTimeMedian`;
    } else if (type === 'resolution') {
      routeAppend = `?chartType=resolutionTimeMedian`;
    } else if (type === 'conversations') {
      routeAppend = ``;
    } else if (type === 'customers') {
      routeAppend = `?dimension=customerId`;
    } else if (type === 'assignee') {
      routeAppend = `?dimension=assigneeId`;
    } else if (type === 'tags') {
      routeAppend = `?dimension=tagId`;
    } else if (type === 'ticketTypes') {
      routeAppend = `?dimension=ticketTypeId`;
    }

    this.navController.navigateRoot(`/dashboard/analytics${routeAppend}`);
  }

  viewInbox() {
    this.navController.navigateRoot(`/${routes.DASHBOARD}/${routes.INBOX}/me`);
  }
}
